import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  ArrowDownCircle,
  ArrowUpCircle,
  ChevronRight,
  Cog,
  Search,
  X,
} from "lucide-react";
import { getRoleHistory } from "../../../redux/admin/action";
import AdminTabs from "./AdminTabs";
import CommonPagination from "../../Common/Pagination/CommonPagination";
import "./AdminList.css";
import "./RoleHistoryList.css";

const PER_PAGE = 20;
const DEBOUNCE_MS = 350;
const SYSTEM_ACTOR_ID = 0;

const ACTION_OPTIONS = [
  { value: "", label: "All actions" },
  { value: "promotion", label: "Promotions" },
  { value: "demotion", label: "Demotions" },
];

const formatDateTime = (raw) => {
  if (!raw) return "—";
  const d = new Date(raw);
  if (Number.isNaN(d.getTime())) return raw;
  return d.toLocaleString(undefined, {
    month: "short",
    day: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

// Render a participant (target or actor) as a label. The backend guarantees
// `id` always exists; `name`/`email_id` are null when `deleted: true`.
// Actor id=0 is the System pseudo-user and gets a glyph instead of a name.
const renderParticipant = (p, { isActor = false } = {}) => {
  if (!p) return <span className="role-history__muted">Unknown</span>;
  if (isActor && p.id === SYSTEM_ACTOR_ID) {
    return (
      <span className="role-history__system">
        <Cog size={12} aria-hidden="true" /> System
      </span>
    );
  }
  if (p.deleted) {
    return (
      <span className="role-history__deleted">admin #{p.id} (deleted)</span>
    );
  }
  return <span className="role-history__name">{p.name || `Admin #${p.id}`}</span>;
};

const ActionChip = ({ action }) => {
  const isPromotion = action === "promotion";
  const Icon = isPromotion ? ArrowUpCircle : ArrowDownCircle;
  return (
    <span
      className={`role-history__chip role-history__chip--${
        isPromotion ? "promote" : "demote"
      }`}
    >
      <Icon size={12} aria-hidden="true" />
      {isPromotion ? "Granted" : "Removed"}
    </span>
  );
};

const buildParams = (search) => {
  const out = {
    page: Number(search.get("page")) || 1,
    per_page: PER_PAGE,
  };
  const action = search.get("action");
  if (action) out.action = action;
  const fromDate = search.get("from_date");
  if (fromDate) out.from_date = fromDate;
  const toDate = search.get("to_date");
  if (toDate) out.to_date = toDate;
  const searchKey = search.get("search_key");
  if (searchKey) out.search_key = searchKey;
  return out;
};

const RoleHistoryList = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [search, setSearch] = useSearchParams();

  const params = useMemo(() => buildParams(search), [search]);
  const [searchDraft, setSearchDraft] = useState(params.search_key || "");

  const data = useSelector(({ admin: { roleHistory } }) => roleHistory);
  const rows = data?.history || [];
  const meta = data?.pagination_meta;

  useEffect(() => {
    dispatch(getRoleHistory(params));
  }, [dispatch, params]);

  // Debounced search → URL. Trim before write so " " doesn't trigger a fetch.
  useEffect(() => {
    const handle = setTimeout(() => {
      const next = new URLSearchParams(search);
      const trimmed = searchDraft.trim();
      if (trimmed === (search.get("search_key") || "")) return;
      if (trimmed) next.set("search_key", trimmed);
      else next.delete("search_key");
      next.set("page", "1");
      setSearch(next, { replace: true });
    }, DEBOUNCE_MS);
    return () => clearTimeout(handle);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchDraft]);

  const updateParam = (key, value) => {
    const next = new URLSearchParams(search);
    if (value) next.set(key, value);
    else next.delete(key);
    next.set("page", "1");
    setSearch(next, { replace: false });
  };

  const handlePageChange = (page) => {
    const next = new URLSearchParams(search);
    next.set("page", String(page));
    setSearch(next);
  };

  const clearSearch = () => {
    setSearchDraft("");
  };

  const openTarget = (target) => {
    if (!target || target.deleted) return;
    navigate(`/admin/view/${target.id}`);
  };

  return (
    <div className="admin-list">
      <header className="admin-list__header">
        <div>
          <h2 className="admin-list__title">Admins</h2>
          <p className="admin-list__subtitle">
            Manage dashboard access and per-module permissions.
          </p>
        </div>
      </header>

      <AdminTabs />

      <div className="admin-list__toolbar">
        <div className="admin-list__search">
          <Search size={16} className="admin-list__search-icon" />
          <input
            type="text"
            value={searchDraft}
            onChange={(e) => setSearchDraft(e.target.value)}
            placeholder="Search by name or email…"
            aria-label="Search role history"
          />
          {searchDraft && (
            <button
              type="button"
              className="admin-list__search-clear"
              aria-label="Clear search"
              onClick={clearSearch}
            >
              <X size={14} />
            </button>
          )}
        </div>
        <select
          className="admin-list__status-filter"
          value={params.action || ""}
          onChange={(e) => updateParam("action", e.target.value)}
          aria-label="Filter by action"
        >
          {ACTION_OPTIONS.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
        <input
          type="date"
          className="role-history__date"
          value={params.from_date || ""}
          onChange={(e) => updateParam("from_date", e.target.value)}
          aria-label="From date"
          max={params.to_date || undefined}
        />
        <input
          type="date"
          className="role-history__date"
          value={params.to_date || ""}
          onChange={(e) => updateParam("to_date", e.target.value)}
          aria-label="To date"
          min={params.from_date || undefined}
        />
      </div>

      <div className="admin-list__table-wrap">
        <table className="admin-list__table">
          <thead>
            <tr>
              <th scope="col" className="role-history__col-when">
                When
              </th>
              <th scope="col" className="role-history__col-action">
                Action
              </th>
              <th scope="col">Change</th>
              <th
                scope="col"
                className="role-history__col-go"
                aria-label="Open target admin"
              />
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr>
                <td colSpan={4} className="admin-list__empty">
                  No role changes match your filters.
                </td>
              </tr>
            ) : (
              rows.map((row) => {
                const clickable = !row.target?.deleted;
                return (
                  <tr
                    key={row.id}
                    className={`role-history__row${
                      clickable ? " role-history__row--clickable" : ""
                    }`}
                    onClick={() => openTarget(row.target)}
                    onKeyDown={(e) => {
                      if (!clickable) return;
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        openTarget(row.target);
                      }
                    }}
                    tabIndex={clickable ? 0 : -1}
                    role={clickable ? "button" : undefined}
                  >
                    <td className="role-history__cell-when">
                      {formatDateTime(row.created_on)}
                    </td>
                    <td>
                      <ActionChip action={row.action} />
                    </td>
                    <td>
                      <div className="role-history__sentence">
                        {renderParticipant(row.actor, { isActor: true })}
                        {row.action === "promotion" ? (
                          <>
                            <span className="role-history__connector">made</span>
                            {renderParticipant(row.target)}
                            <span className="role-history__connector">
                              a Super Admin
                            </span>
                          </>
                        ) : (
                          <>
                            <span className="role-history__connector">
                              removed Super Admin from
                            </span>
                            {renderParticipant(row.target)}
                          </>
                        )}
                      </div>
                    </td>
                    <td className="role-history__col-go">
                      {clickable && (
                        <ChevronRight
                          size={16}
                          className="role-history__chevron"
                          aria-hidden="true"
                        />
                      )}
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {meta && meta.pages > 1 && (
        <footer className="admin-list__footer">
          <CommonPagination
            currentPage={meta.current_page}
            totalPages={meta.pages}
            onPageChange={handlePageChange}
          />
        </footer>
      )}
    </div>
  );
};

export default RoleHistoryList;
